import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { motion } from 'framer-motion'

import { Button } from '@cockpitai/components/atoms'
import { Select } from '@cockpitai/components/atoms/Select'
import { Typography } from '@cockpitai/components/atoms/Typography'
import { ModalStatic } from '@cockpitai/components/organisms/Modal/ModalStatic'
import { ScheduleDate } from './ScheduleDate'

import { InputCurrency } from '@cockpitai/components/atoms/InputCurrency'
import { ComboBoxPeople } from './ComboBoxPeople'
import { useCardMenuStore } from '@cockpitai/store/cardMenu'
import { useCreateAlertForModuleMutation } from '@cockpitai/integration/alerts/hooks/useCreateAlertForModuleMutation'
import { useToast } from '@cockpitai/hooks/useToast'
import { ComboBoxPlatform } from './ComboBoxPlatform'
import { useQueryClient } from '@tanstack/react-query'

const options = [
  {
    value: 'bigger',
    title: 'Maior',
  },
  {
    value: 'bigger-than',
    title: 'Maior que',
  },
  {
    value: 'equal',
    title: 'Igual',
  },
  {
    value: 'less',
    title: 'Menor',
  },
  {
    value: 'less-or-equal',
    title: 'Menor Igual',
  },
]

const schema = z.object({
  period: z
    .object({
      endDate: z.optional(z.string()),
      endHour: z.optional(z.string()),
      endMonth: z.optional(z.string()),
      endYear: z.optional(z.string()),
      option: z.optional(z.number()),
    })
    .refine(
      (schema) => {
        console.log(schema)

        if (
          !('endDate' in schema) &&
          !('endMonth' in schema) &&
          !('endYear' in schema)
        ) {
          return false
        }

        return true
      },
      {
        message: 'Selecione um período',
      },
    ),
  condition: z.object({
    title: z.string(),
    value: z.string(),
  }),
  value: z.string().min(1, 'Digite um valor'),
  people: z
    .array(
      z.object({
        label: z.string(),
        value: z.string(),
      }),
    )
    .min(1, 'Selecione pelo menos uma pessoa'),
  alertChannels: z
    .array(
      z.object({
        label: z.string(),
        value: z.string(),
      }),
    )
    .min(1, 'Selecione pelo menos uma plataforma'),
})

type TCreateAlertFlowFormData = z.infer<typeof schema>

export function CreateAlertFlow() {
  const { mutateAsync, isPending } = useCreateAlertForModuleMutation()

  const queryClient = useQueryClient()

  const { control, handleSubmit, formState, reset } =
    useForm<TCreateAlertFlowFormData>({
      defaultValues: {
        period: {},
        condition: options[0],
        value: '',
        people: [],
        alertChannels: [],
      },
      resolver: zodResolver(schema),
    })

  const { showToast } = useToast()

  const { clear, payload } = useCardMenuStore()

  const onSubmit = async (data: TCreateAlertFlowFormData) => {
    try {
      await mutateAsync({
        graphic: {
          chartType: payload?.chartType,
          chartListType: payload?.chartListType,
        },
        value: parseFloat(data.value),
        condition: data.condition.value,
        period: {
          endDate: data.period?.endDate,
          endMonth: data.period?.endMonth,
          endYear: data.period?.endYear,
        },
        alertChannels: data.alertChannels.map((item) => item.value),
        people: data.people.map((item) => item.value),
      })

      showToast('success', {
        title: 'Alerta criado com sucesso',
        subtitle: 'Você será notificado quando a condição for atingida',
      })

      reset()

      clear()

      queryClient.invalidateQueries({ queryKey: ['alerts-history'] })
    } catch (error) {
      showToast('error', {
        title: 'Erro ao criar alerta',
        subtitle: 'Tente novamente mais tarde',
      })
    }
  }

  const errors = Object.entries(formState.errors).flatMap(
    ([_, value]) => value.message,
  )

  return (
    <ModalStatic show={true} canDismiss={false}>
      <div className="p-6">
        <div className="flex items-center justify-between">
          <Typography type="subtitleTwo" className="text-white/75">
            Criar alerta
          </Typography>
        </div>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col space-y-6 pt-6">
          <div>
            <Typography type="bodyOne" className="text-white/75">
              Quando
            </Typography>
            <div className="mt-2 grid grid-cols-2 gap-1">
              <div className="flex h-10 items-center rounded-lg  bg-gray-800 px-4">
                <Typography type="bodyOne" className="truncate text-gray-200">
                  {payload?.chartTitle}
                </Typography>
              </div>
              <Controller
                name="period"
                control={control}
                render={({ field, fieldState }) => (
                  <ScheduleDate
                    value={field.value}
                    placeholder="período"
                    isInvalid={fieldState.invalid}
                    message={fieldState.error?.message}
                    onChange={field.onChange}
                  />
                )}
              />

              <Controller
                name="condition"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    options={options}
                    placeholder="resultado"
                    onChange={field.onChange}
                  />
                )}
              />

              <Controller
                name="value"
                control={control}
                render={({ field, fieldState }) => (
                  <InputCurrency
                    value={field.value}
                    placeholder="Valor"
                    variant="secondary"
                    onChange={field.onChange}
                    isInvalid={fieldState.invalid}
                  />
                )}
              />
            </div>
          </div>
          <div>
            <Typography
              type="bodyOne"
              className="text-white/75"
              font="outfit"
              weight="regular">
              Então
            </Typography>
            <div className="mt-2 space-y-1">
              <div className="col-span-2 flex space-x-1 ">
                <div className="flex h-10 max-w-fit items-center justify-center rounded-lg bg-gray-800 px-2">
                  <Typography type="bodyOne" className="text-gray-200">
                    Notificar
                  </Typography>
                </div>
                <Controller
                  control={control}
                  name="alertChannels"
                  render={({ field, fieldState }) => (
                    <ComboBoxPlatform
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="local da notificação"
                      isInvalid={fieldState.invalid}
                      message={fieldState.error?.message}
                    />
                  )}
                />
              </div>

              <Controller
                name="people"
                control={control}
                render={({ field, fieldState }) => (
                  <ComboBoxPeople
                    placeholder="pessoas notificadas"
                    value={field.value}
                    onChange={field.onChange}
                    isInvalid={fieldState.invalid}
                    message={fieldState.error?.message}
                  />
                )}
              />
            </div>
          </div>
          {errors.length > 0 && (
            <ul>
              {errors.map((error) => (
                <motion.li
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  key={error}
                  className="font-outfit text-xs text-danger-500">
                  {error}
                </motion.li>
              ))}
            </ul>
          )}
          <div className="flex items-center gap-2">
            <Button type="button" full onClick={clear} isDisabled={isPending}>
              Descartar alerta
            </Button>
            <Button
              colorScheme="secondary"
              full
              isLoading={isPending}
              isDisabled={!formState.isValid || isPending}>
              Salvar alerta
            </Button>
          </div>
        </form>
      </div>
    </ModalStatic>
  )
}
